import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Monitor, Eye, EyeOff, Save, RotateCcw,
  MessageSquare, FolderOpen, ToggleLeft, ToggleRight,
  Layout, BarChart3, FileText, Coins, CheckCircle2,
  Loader2, Smartphone, ClipboardList, Receipt,
  FileQuestion, Home, Shield,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_CONFIG = [
  {
    group: "Sidebar Navigation",
    icon: Layout,
    color: "from-slate-600 to-slate-800",
    desc: "Control which pages are visible in the translator sidebar",
    items: [
      { key: "nav_analytics", label: "Analytics", desc: "Performance stats, charts and breakdowns" },
      { key: "nav_projects", label: "Projects", desc: "Task list with confirm/approve buttons" },
      { key: "nav_assignments", label: "Assignments", desc: "Assignment list with confirm buttons" },
      { key: "nav_earnings", label: "Earnings", desc: "Earnings breakdown by task/assignment type" },
      { key: "nav_invoices", label: "Invoices", desc: "Invoice view with TDS info and PDF download" },
      { key: "nav_missing_projects", label: "Missing Projects", desc: "Report and claim missing projects" },
    ],
  },
  {
    group: "Home Page",
    icon: Home,
    color: "from-teal-500 to-emerald-500",
    desc: "Sections on the translator's welcome/home page",
    items: [
      { key: "home_earnings_hero", label: "Earnings Hero Banner", desc: "Big total earnings display in the welcome hero" },
      { key: "home_quick_actions", label: "Quick Action Cards", desc: "Projects, Assignments, Earnings, Analytics shortcut cards" },
      { key: "home_specializations", label: "Your Specializations", desc: "Task type badges showing translator's work areas" },
      { key: "home_earnings_split", label: "Earnings Split", desc: "Task vs Assignment earnings breakdown card" },
    ],
  },
  {
    group: "Analytics Page",
    icon: BarChart3,
    color: "from-violet-500 to-purple-500",
    desc: "Sections within the Analytics view",
    items: [
      { key: "analytics_kpi_cards", label: "KPI Cards", desc: "Total Earned and Total Tasks summary cards" },
      { key: "analytics_work_progress", label: "Work Progress", desc: "Completion ring with duration stats" },
      { key: "analytics_turnaround", label: "Turnaround Stats", desc: "Average turnaround time display" },
      { key: "analytics_invoiced_paid", label: "Invoiced vs Paid", desc: "Invoice and payment summary" },
      { key: "analytics_earnings_chart", label: "Earnings by Project", desc: "Bar chart of earnings per project" },
      { key: "analytics_task_distribution", label: "Task Type Distribution", desc: "Task type cards with progress bars" },
    ],
  },
  {
    group: "Invoice Settings",
    icon: Receipt,
    color: "from-indigo-500 to-blue-500",
    desc: "Control what translators see in their invoice view",
    items: [
      { key: "invoice_tds_display", label: "TDS Breakdown", desc: "Show TDS calculation details and reason" },
      { key: "invoice_pdf_download", label: "PDF Download", desc: "Allow translators to download invoice PDFs" },
    ],
  },
  {
    group: "Data & Privacy",
    icon: Shield,
    color: "from-amber-500 to-orange-500",
    desc: "Control data visibility and UI elements",
    items: [
      { key: "show_amounts", label: "Show Earnings & Amounts", desc: "Display monetary values to translators" },
      { key: "show_missing_banner", label: "Missing Project Banner", desc: "Show 'Is something missing?' prompt on pages" },
    ],
  },
];

// ============= SIDEBAR PREVIEW =============
const TranslatorPreview = ({ sections, welcomeMessage, visibleProjects }) => {
  const isOn = (key) => sections[key] !== false;

  const navItems = [
    { key: "nav_analytics", label: "Analytics", icon: BarChart3 },
    { key: "nav_projects", label: "Projects", icon: FileText },
    { key: "nav_assignments", label: "Assignments", icon: ClipboardList },
    { key: "nav_earnings", label: "Earnings", icon: Coins },
    { key: "nav_invoices", label: "Invoices", icon: Receipt },
  ];
  const visibleNavItems = navItems.filter(n => isOn(n.key));

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-slate-900 rounded-[20px] p-1.5 shadow-2xl">
        <div className="bg-slate-50 rounded-[16px] overflow-hidden flex" style={{ minHeight: 420 }}>
          {/* Sidebar */}
          <div className="w-[72px] bg-slate-900 flex flex-col py-3 px-1.5 flex-shrink-0">
            <div className="w-8 h-8 mx-auto rounded-lg bg-teal-500/20 flex items-center justify-center mb-3">
              <span className="text-[8px] font-bold text-teal-400">G</span>
            </div>
            {/* Home */}
            <button className="w-full py-1.5 rounded-md bg-teal-500/20 border border-teal-500/30 mb-1">
              <Home className="w-3 h-3 text-teal-400 mx-auto" />
              <p className="text-[6px] text-teal-300 mt-0.5 text-center">Home</p>
            </button>
            {visibleNavItems.map(({ key, label, icon: Icon }) => (
              <button key={key} className="w-full py-1.5 rounded-md hover:bg-slate-800 mb-0.5">
                <Icon className="w-3 h-3 text-slate-500 mx-auto" />
                <p className="text-[6px] text-slate-500 mt-0.5 text-center">{label}</p>
              </button>
            ))}
            {isOn("nav_missing_projects") && (
              <div className="mt-2 pt-2 border-t border-slate-700/50">
                <button className="w-full py-1.5 rounded-md hover:bg-slate-800">
                  <FileQuestion className="w-3 h-3 text-slate-500 mx-auto" />
                  <p className="text-[6px] text-slate-500 mt-0.5 text-center">Missing</p>
                </button>
              </div>
            )}
          </div>

          {/* Main Content */}
          <div className="flex-1 p-3 overflow-hidden">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-slate-200">
              <div>
                <p className="text-[10px] font-bold text-slate-800">Welcome, Baranidharan</p>
                <p className="text-[7px] text-slate-400">
                  {welcomeMessage || "Your personalized dashboard"}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[7px] text-slate-400">EARNED</p>
                <p className="text-[9px] font-bold text-emerald-600">{isOn("show_amounts") ? "₹3,200" : "****"}</p>
              </div>
            </div>

            {/* Home Page Preview */}
            {isOn("home_earnings_hero") && (
              <div className="bg-gradient-to-br from-slate-800 to-teal-900 rounded-lg p-3 mb-2">
                <p className="text-[7px] text-teal-400">Total Earnings</p>
                <p className="text-base font-extrabold text-white">{isOn("show_amounts") ? "₹3,200.00" : "****"}</p>
                <div className="flex gap-3 mt-1.5">
                  <div><p className="text-[6px] text-slate-500">Tasks</p><p className="text-[9px] font-bold text-white">91</p></div>
                  <div><p className="text-[6px] text-slate-500">Assignments</p><p className="text-[9px] font-bold text-white">0</p></div>
                  <div><p className="text-[6px] text-slate-500">Confirmed</p><p className="text-[9px] font-bold text-teal-400">0</p></div>
                </div>
              </div>
            )}

            {isOn("home_quick_actions") && (
              <div className="grid grid-cols-4 gap-1 mb-2">
                {["Projects", "Assigns", "Earnings", "Analytics"].map((label, i) => (
                  <div key={i} className="bg-white rounded-md border border-slate-200 p-1.5 text-center">
                    <div className={`w-4 h-4 mx-auto rounded-md mb-0.5 ${["bg-blue-100","bg-indigo-100","bg-amber-100","bg-emerald-100"][i]} flex items-center justify-center`}>
                      {[<FileText />, <ClipboardList />, <Coins />, <BarChart3 />][i] &&
                        React.createElement([FileText, ClipboardList, Coins, BarChart3][i], { className: `w-2.5 h-2.5 ${["text-blue-500","text-indigo-500","text-amber-500","text-emerald-500"][i]}` })}
                    </div>
                    <p className="text-[6px] font-semibold text-slate-600">{label}</p>
                  </div>
                ))}
              </div>
            )}

            {isOn("home_specializations") && (
              <div className="bg-white rounded-md border border-slate-200 p-2 mb-2">
                <p className="text-[7px] font-semibold text-slate-600 mb-1">Specializations</p>
                <div className="flex flex-wrap gap-0.5">
                  {["QC", "Rfrm_S", "QC_MT"].map((t, i) => (
                    <span key={i} className="px-1.5 py-0.5 bg-slate-100 rounded text-[6px] font-medium text-slate-600">{t}</span>
                  ))}
                </div>
              </div>
            )}

            {isOn("home_earnings_split") && (
              <div className="bg-white rounded-md border border-slate-200 p-2 mb-2">
                <p className="text-[7px] font-semibold text-slate-600 mb-1">Earnings Split</p>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[6px] text-slate-500 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Tasks</span>
                    <span className="text-[7px] font-bold text-slate-700">{isOn("show_amounts") ? "₹3,200" : "****"}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[6px] text-slate-500 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" />Assignments</span>
                    <span className="text-[7px] font-bold text-slate-700">{isOn("show_amounts") ? "₹0" : "****"}</span>
                  </div>
                </div>
              </div>
            )}

            {isOn("show_missing_banner") && (
              <div className="bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mb-2">
                <p className="text-[7px] font-semibold text-amber-800">Is something missing?</p>
                <p className="text-[6px] text-amber-600">Report missing projects</p>
              </div>
            )}

            {/* Project filter note */}
            {visibleProjects.length > 0 && (
              <div className="px-2 py-1.5 bg-cyan-50 rounded-md border border-cyan-200">
                <p className="text-[7px] font-semibold text-cyan-700 mb-0.5">Visible Projects Filter</p>
                <div className="flex flex-wrap gap-0.5">
                  {visibleProjects.slice(0, 4).map((p, i) => (
                    <span key={i} className="text-[6px] px-1 py-0.5 bg-white rounded border border-cyan-100 text-cyan-600 truncate max-w-[80px]">{p}</span>
                  ))}
                  {visibleProjects.length > 4 && (
                    <span className="text-[6px] px-1 py-0.5 bg-cyan-100 rounded text-cyan-600">+{visibleProjects.length - 4}</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= MAIN COMPONENT =============
const PortalControl = () => {
  const [settings, setSettings] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [welcomeMessage, setWelcomeMessage] = useState("");
  const [visibleProjects, setVisibleProjects] = useState([]);
  const [sections, setSections] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/portal-settings`);
      const s = res.data.settings;
      setSettings(s);
      setWelcomeMessage(s.welcome_message || "");
      setVisibleProjects(s.visible_projects || []);
      setSections(s.sections || {});
      setAvailableProjects(res.data.available_projects || []);
      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to load portal settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const toggleSection = (key) => {
    setSections(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
    setHasChanges(true);
  };

  const toggleProject = (project) => {
    setVisibleProjects(prev => {
      const next = prev.includes(project)
        ? prev.filter(p => p !== project)
        : [...prev, project];
      setHasChanges(true);
      return next;
    });
  };

  const selectAllProjects = () => { setVisibleProjects([...availableProjects]); setHasChanges(true); };
  const clearAllProjects = () => { setVisibleProjects([]); setHasChanges(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/portal-settings`, {
        welcome_message: welcomeMessage,
        visible_projects: visibleProjects,
        sections,
      });
      toast.success("Portal settings saved — changes are live!");
      setHasChanges(false);
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setWelcomeMessage(settings.welcome_message || "");
      setVisibleProjects(settings.visible_projects || []);
      setSections(settings.sections || {});
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="portal-control-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Monitor className="w-6 h-6 text-blue-500" />
            Portal Control
          </h1>
          <p className="page-subtitle">Configure what translators see in their portal. Changes apply in real-time.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPreview(true)}
            className="border-violet-200 text-violet-700 hover:bg-violet-50"
            data-testid="preview-btn"
          >
            <Smartphone className="w-3.5 h-3.5 mr-1.5" />
            Preview
          </Button>
          {hasChanges && (
            <Button variant="outline" size="sm" onClick={handleReset} data-testid="reset-btn">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Reset
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            size="sm"
            data-testid="save-settings-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="mb-5 flex items-center gap-2 px-3.5 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700">
          <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          Unsaved changes — click Save to apply
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Section Toggles */}
        <div className="lg:col-span-2 space-y-5">
          {SECTION_CONFIG.map((group) => {
            const GIcon = group.icon;
            const allOn = group.items.every(i => sections[i.key] !== false);
            return (
              <Card key={group.group} className="border-slate-200 shadow-sm overflow-hidden">
                <CardHeader className="pb-3 pt-4 px-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${group.color} flex items-center justify-center`}>
                          <GIcon className="w-3.5 h-3.5 text-white" />
                        </div>
                        {group.group}
                      </CardTitle>
                      {group.desc && <p className="text-[10px] text-slate-400 mt-1 ml-9">{group.desc}</p>}
                    </div>
                    <button
                      onClick={() => {
                        const newVal = !allOn;
                        const updated = { ...sections };
                        group.items.forEach(i => { updated[i.key] = newVal; });
                        setSections(updated);
                        setHasChanges(true);
                      }}
                      className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 transition-colors px-2 py-1 rounded-md hover:bg-slate-100"
                      data-testid={`toggle-all-${group.group.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      {allOn ? "HIDE ALL" : "SHOW ALL"}
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="px-5 pb-4 pt-0">
                  <div className="space-y-1">
                    {group.items.map((item) => {
                      const isOn = sections[item.key] !== false;
                      return (
                        <div
                          key={item.key}
                          className={`flex items-center justify-between p-3 rounded-xl transition-all cursor-pointer ${
                            isOn ? "bg-slate-50 hover:bg-slate-100" : "bg-red-50/50 hover:bg-red-50"
                          }`}
                          onClick={() => toggleSection(item.key)}
                          data-testid={`toggle-${item.key}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium ${isOn ? "text-slate-700" : "text-slate-400"}`}>
                                {item.label}
                              </p>
                              {!isOn && (
                                <Badge variant="outline" className="text-[9px] border-red-200 text-red-500 bg-red-50 px-1.5 py-0">
                                  HIDDEN
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">{item.desc}</p>
                          </div>
                          <div className="flex-shrink-0 ml-3">
                            {isOn ? (
                              <div className="flex items-center gap-1.5 text-emerald-600">
                                <Eye className="w-4 h-4" />
                                <ToggleRight className="w-6 h-6" />
                              </div>
                            ) : (
                              <div className="flex items-center gap-1.5 text-slate-300">
                                <EyeOff className="w-4 h-4" />
                                <ToggleLeft className="w-6 h-6" />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Right Column: Welcome Message + Project Visibility */}
        <div className="space-y-5">
          {/* Welcome Message */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center">
                  <MessageSquare className="w-3.5 h-3.5 text-white" />
                </div>
                Welcome Message
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <Label className="text-xs text-slate-500 mb-1.5 block">
                Custom subtitle shown below translator's name on the home page
              </Label>
              <Textarea
                value={welcomeMessage}
                onChange={(e) => { setWelcomeMessage(e.target.value); setHasChanges(true); }}
                placeholder="invoicing.graphikpro.com - A Product of emergent.sh"
                className="text-sm resize-none h-20"
                data-testid="welcome-message-input"
              />
              <p className="text-[10px] text-slate-400 mt-1.5">{welcomeMessage.length}/120 characters</p>
            </CardContent>
          </Card>

          {/* Project Visibility */}
          <Card className="border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="pb-3 pt-4 px-5">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-cyan-500 to-sky-500 flex items-center justify-center">
                  <FolderOpen className="w-3.5 h-3.5 text-white" />
                </div>
                Project Visibility
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 pt-0">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500">
                  {visibleProjects.length === 0
                    ? "All projects visible (no filter)"
                    : `${visibleProjects.length} of ${availableProjects.length} visible`}
                </p>
                <div className="flex gap-1.5">
                  <button onClick={selectAllProjects} className="text-[10px] font-semibold text-blue-600 hover:text-blue-800 px-1.5 py-0.5 rounded hover:bg-blue-50" data-testid="select-all-projects">All</button>
                  <button onClick={clearAllProjects} className="text-[10px] font-semibold text-slate-400 hover:text-slate-600 px-1.5 py-0.5 rounded hover:bg-slate-100" data-testid="clear-all-projects">None</button>
                </div>
              </div>

              {visibleProjects.length === 0 && (
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                  <p className="text-[11px] text-blue-600 font-medium">No filter active — translators see all projects</p>
                  <p className="text-[10px] text-blue-400 mt-0.5">Select specific projects below to restrict visibility</p>
                </div>
              )}

              <div className="max-h-72 overflow-y-auto space-y-1 pr-1 custom-scrollbar">
                {availableProjects.map((project) => {
                  const isSelected = visibleProjects.includes(project);
                  return (
                    <div
                      key={project}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
                        isSelected
                          ? "bg-cyan-50 border border-cyan-200 text-cyan-700 font-medium"
                          : "bg-slate-50 border border-transparent text-slate-500 hover:bg-slate-100"
                      }`}
                      onClick={() => toggleProject(project)}
                      data-testid={`project-${project.replace(/\s/g, '-').toLowerCase()}`}
                    >
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected ? "border-cyan-500 bg-cyan-500" : "border-slate-300"
                      }`}>
                        {isSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                      </div>
                      <span className="truncate">{project}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200">
            <p className="text-xs font-semibold text-slate-700 mb-1">Real-time Preview</p>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Changes take effect immediately after saving. Click "Preview" to see the current layout.
            </p>
            {settings?.updated_at && (
              <p className="text-[10px] text-slate-400 mt-2">
                Last updated: {new Date(settings.updated_at).toLocaleString('en-IN')}
                {settings.updated_by && ` by ${settings.updated_by}`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="preview-dialog">
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-violet-500" />
              Translator Portal Preview
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Live preview with your current toggle settings</DialogDescription>
          </DialogHeader>
          <div className="px-5 pb-5 max-h-[70vh] overflow-y-auto">
            <TranslatorPreview
              sections={sections}
              welcomeMessage={welcomeMessage}
              visibleProjects={visibleProjects}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortalControl;
